import {union, without} from 'lodash';

import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {
  buildCreateContactFormEmail,
  buildCreateContactFormPhone,
  buildCreateContactFormTwitterField,
  buildCreateContactLinkField,
} from '../../../../helpers/createContactFormHelpers';
import {AppAction} from '../../../appActions';
import {ContactsManagerCreateContactFormState} from '../contactsManager/sidePanel/contactsManagerCreateContactFormState';
import {CreateContactFormActionsEnum} from './createContactFormActions';
import {
  CreateContactFormFieldTypesEnum,
  CreateContactFormFieldValidityStatusesEnum,
} from './createContactFormState';

export function contactsManagerCreateContactFormReducer(
  state: ContactsManagerCreateContactFormState,
  action: AppAction,
): ContactsManagerCreateContactFormState | undefined {
  switch (action.type) {
    case CreateContactFormActionsEnum.UPDATE_NAME: {
      return {
        ...state,
        name: action.payload.name,
      };
    }
    case CreateContactFormActionsEnum.UPDATE_DESCRIPTION: {
      return {
        ...state,
        description: action.payload.description,
      };
    }
    case CreateContactFormActionsEnum.UPDATE_AVATAR_FILE: {
      return {
        ...state,
        avatarFile: action.payload.avatarFile,
      };
    }
    case CreateContactFormActionsEnum.UPDATE_ACCESS: {
      return {
        ...state,
        access: action.payload.access,
        accountId: undefined,
      };
    }
    case CreateContactFormActionsEnum.UPDATE_ACCOUNT: {
      return {
        ...state,
        accountId: action.payload.accountId,
      };
    }
    case CreateContactFormActionsEnum.UPDATE_CUSTOM_ATTRIBUTE: {
      return {
        ...state,
        customAttributes: state.customAttributes.map((customAttribute) =>
          customAttribute.id === action.payload.customFieldId
            ? {...customAttribute, value: action.payload.value}
            : customAttribute,
        ),
      };
    }

    case CreateContactFormActionsEnum.UPDATE_LINK: {
      return {
        ...state,
        links: state.links.map((link) => {
          if (link.uid === action.payload.uid) {
            return {
              ...link,
              value: action.payload.value,
            };
          }
          return link;
        }),
      };
    }
    case CreateContactFormActionsEnum.LINK_ADDED: {
      return {
        ...state,
        links: [...state.links, buildCreateContactLinkField()],
      };
    }
    case CreateContactFormActionsEnum.LINK_REMOVED: {
      return {
        ...state,
        links: state.links.filter((link) => link.uid !== action.payload.uid),
      };
    }

    case CreateContactFormActionsEnum.CARD_GROUP_ADDED:
      return {
        ...state,
        cardGroupIds: union(state.cardGroupIds, [action.payload.cardGroupId]),
      };
    case CreateContactFormActionsEnum.CARD_GROUP_REMOVED:
      return {
        ...state,
        cardGroupIds: without(state.cardGroupIds, action.payload.cardGroupId),
      };

    case CreateContactFormActionsEnum.UPDATE_CONTACT_FIELD: {
      return {
        ...state,
        contactFields: state.contactFields.map((field) => {
          if (field.uid === action.payload.uid) {
            return {
              ...field,
              value: action.payload.value,
              validity: CreateContactFormFieldValidityStatusesEnum.TO_BE_CHECKED,
            };
          }
          return field;
        }),
      };
    }
    case CreateContactFormActionsEnum.CONTACT_FIELD_VALIDITY_UPDATED: {
      return {
        ...state,
        contactFields: state.contactFields.map((field) => {
          if (field.uid === action.payload.uid) {
            return {
              ...field,
              validity: action.payload.validity,
              duplicateCardId: action.payload.duplicateCardId ?? field.duplicateCardId,
            };
          }
          return field;
        }),
      };
    }
    case CreateContactFormActionsEnum.CONTACT_FIELD_ADDED: {
      switch (action.payload.type) {
        case CreateContactFormFieldTypesEnum.EMAIL:
          return {
            ...state,
            contactFields: [...state.contactFields, buildCreateContactFormEmail()],
          };
        case CreateContactFormFieldTypesEnum.PHONE_NUMBER:
          return {
            ...state,
            contactFields: [...state.contactFields, buildCreateContactFormPhone()],
          };
        case CreateContactFormFieldTypesEnum.TWITTER:
          return {
            ...state,
            contactFields: [...state.contactFields, buildCreateContactFormTwitterField()],
          };
        default:
          return state;
      }
    }
    case CreateContactFormActionsEnum.CONTACT_FIELD_REMOVED: {
      return {
        ...state,
        contactFields: state.contactFields.filter((field) => field.uid !== action.payload.uid),
      };
    }

    case CreateContactFormActionsEnum.CREATE_CONTACT_STARTED: {
      return {
        ...state,
        createStatus: AsyncStatusesEnum.LOADING,
      };
    }
    case CreateContactFormActionsEnum.CREATE_CONTACT_COMPLETED: {
      return {
        ...state,
        createStatus: AsyncStatusesEnum.LOADED,
      };
    }
    case CreateContactFormActionsEnum.CREATE_CONTACT_FAILED: {
      return {
        ...state,
        createStatus: AsyncStatusesEnum.FAILED,
      };
    }

    default: {
      return state;
    }
  }
}
